<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h3>{{ $t('bo.esco.skillGroupEdit.title') }}</h3>
        <button @click="$emit('close')" class="btn-close">&times;</button>
      </div>

      <div class="modal-body">
        <div v-if="error" class="error-banner">{{ error }}</div>

        <!-- Name -->
        <div class="form-group">
          <label class="form-label">{{ $t('bo.esco.skillGroupEdit.groupName') }}</label>
          <input
            v-model="form.name"
            type="text"
            class="form-input"
            :placeholder="$t('bo.esco.skillGroupEdit.groupNamePlaceholder')"
          />
        </div>

        <!-- Category (group_type) -->
        <div class="form-group">
          <label class="form-label">{{ $t('bo.esco.skillGroupEdit.category') }}</label>
          <select v-model="form.group_type" class="form-input">
            <option :value="null">{{ $t('bo.common.none') }}</option>
            <option value="domain">{{ $t('bo.platformSkills.domain') }}</option>
            <option value="transversal">{{ $t('bo.platformSkills.transversal') }}</option>
          </select>
        </div>
      </div>

      <div class="modal-footer">
        <button @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.cancel') }}</button>
        <button @click="save" :disabled="saving || !form.name.trim()" class="btn-save">
          {{ saving ? $t('bo.common.saving') : $t('bo.common.save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import escoService from '@/services/escoService'

const { t } = useI18n()

const props = defineProps({
  group: { type: Object, required: true },
})
const emit = defineEmits(['close', 'saved'])

const form = ref({
  name: props.group.parent_title,
  group_type: props.group.group_type ?? null,
})
const saving = ref(false)
const error = ref('')

const save = async () => {
  const name = form.value.name.trim()
  if (!name) return
  saving.value = true
  error.value = ''
  try {
    const payload = {}
    if (name !== props.group.parent_title) payload.name = name
    if (form.value.group_type !== (props.group.group_type ?? null)) payload.group_type = form.value.group_type
    if (Object.keys(payload).length === 0) { emit('close'); return }
    const res = await escoService.updateSkillGroup(props.group.group_key, payload)
    emit('saved', res.data)
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.common.failedSave')
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 440px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.modal-header h3 { margin: 0; font-size: 1rem; font-weight: 600; color: #111827; }
.btn-close {
  background: none; border: none; font-size: 1.25rem;
  cursor: pointer; color: #6b7280; line-height: 1;
}
.btn-close:hover { color: #111827; }

.modal-body { padding: 1.25rem 1.5rem; display: flex; flex-direction: column; gap: 1rem; overflow-y: auto; flex: 1; }

.form-group { display: flex; flex-direction: column; gap: 0.4rem; }
.form-label { font-size: 0.85rem; font-weight: 600; color: #374151; }
.form-input {
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background: white;
  width: 100%;
}
.form-input:focus { outline: none; border-color: #3b82f6; }

.error-banner { background: #fee2e2; color: #991b1b; padding: 0.5rem 0.75rem; border-radius: 0.375rem; font-size: 0.85rem; }

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.btn-cancel {
  padding: 0.45rem 1rem;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
}
.btn-cancel:hover { background: #f9fafb; }
.btn-save {
  padding: 0.45rem 1.25rem;
  border: none;
  background: #3b82f6;
  color: white;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}
.btn-save:hover:not(:disabled) { background: #2563eb; }
.btn-save:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
